import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { HAS_INSTALLED_MEMORY_TRACKER } from 'constants/user';
import { makeEmptyEnrichedProject } from 'factories/projects';
import { suggestionAsCardFactory } from 'factories/suggestions';
import { entryToMemoryFactory, groupMemoriesFactory } from 'factories/timeline';
import { isEqual, uniq } from 'lodash-es';
import type { Suggester } from 'redux/modules/suggestions.types';
import type { SyntheticState } from 'redux/modules/synthetic';
import { sliceName as syntheticSliceName } from 'redux/modules/synthetic.constants';
import { getCurrentAccount } from 'redux/selectors/accounts';
import { getProjectVariantMap } from 'redux/selectors/projects';
import { getFilter, getMemoryGroupsWithProjects } from 'redux/selectors/timeline';
import { getMemoryEntries, getRelevantMemoryFilters } from 'redux/selectors/timeline.filters';
import { getUsers } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Suggestion, SuggestionAsCard } from 'types/suggestion';
import type { Memory } from 'types/timeline';
import { OperatorMode } from 'types/timeline';
import { prefixHexColor } from 'utils/colors';
import { fromISO } from 'utils/luxon-helpers';
import { getProjectColor } from 'utils/project';
import { getInjectSyntheticData } from './development';
import { getPinnedProjectIdsSet } from './pins';
import { getUserProperties } from './user';
import { enrichProjectVariantOrNull, extractProjectBase } from './utils/projects';

const readSuggestionEntities = (state: IState) => state.suggestions.entities;
const readSuggestionResult = (state: IState) => state.suggestions.result;
const readAccountSettings = (state: IState) => state.user.accountSettings;
const readSuggesters = (state: IState) => state.suggestions.entities.suggesters;

const readSyntheticSuggestionEntities = (state: { [syntheticSliceName]?: SyntheticState }) =>
  (state[syntheticSliceName]?.suggestions.entities ?? EMPTY_OBJECT) as Record<
    number,
    Suggestion<number>
  >;
const readSyntheticSuggestionResult = (state: { [syntheticSliceName]?: SyntheticState }) =>
  (state[syntheticSliceName]?.suggestions.ids ?? EMPTY_ARRAY) as Array<number>;

// Circular dependency workaround
// This is duplicated in hours.draft, but cannot be imported from there. Maybe because
// getHourDraftSuggestionId is used by getHourDraftSuggestion,
// which uses getSuggestionEntities from this file?
const readHourDraftSuggestionId = (state: IState) => state.hours.draft?.suggestion_id || null;
const getHourDraftSuggestionId = readHourDraftSuggestionId;

export const getSuggestionEntities = createSelector(
  [readSuggestionEntities, readSyntheticSuggestionEntities, getInjectSyntheticData],
  (entities, syntheticEntities, injectSyntheticData): Record<number, Suggestion<number>> => {
    if (!injectSyntheticData || !Object.keys(syntheticEntities).length) {
      return entities.suggestions;
    }

    return { ...entities.suggestions, ...syntheticEntities };
  }
);

export const getSuggestionResult = createSelector(
  [readSuggestionResult, readSyntheticSuggestionResult, getInjectSyntheticData],
  (result, syntheticResult, injectSyntheticData) => {
    if (!injectSyntheticData || !syntheticResult.length) {
      return result;
    }

    return syntheticResult.concat(result);
  }
);

const getSyntheticSuggesters = createSelector(
  [getUsers, readSyntheticSuggestionEntities, getInjectSyntheticData],
  (users, syntheticEntities, injectSyntheticData): Array<Suggester> => {
    if (!injectSyntheticData || !Object.keys(syntheticEntities).length) {
      return EMPTY_ARRAY;
    }

    const syntheticUserIds = uniq(
      Object.values(syntheticEntities).map(suggestion => suggestion.owner)
    );

    return syntheticUserIds.reduce<Array<Suggester>>((suggesters, userId) => {
      const user = users.find(user => user.id === userId);
      if (user) {
        suggesters.push({
          id: user.id,
          email: user.email,
          name: user.name,
          avatar: user.avatar,
          updated_at: user.updated_at,
        });
      }

      return suggesters;
    }, []);
  }
);

const getSuggesters = createSelector(
  [readSuggesters, getSyntheticSuggesters, getInjectSyntheticData],
  (suggesters, syntheticSuggesters, injectSyntheticData) => {
    if (!injectSyntheticData || !syntheticSuggesters.length) {
      return suggesters;
    }

    return syntheticSuggesters.reduce<typeof suggesters>(
      (combinedSuggesters, syntheticSuggester) => {
        combinedSuggesters[syntheticSuggester.id] ??= syntheticSuggester;
        return combinedSuggesters;
      },
      { ...(suggesters ?? {}) }
    );
  }
);

export const getSuggestions = createSelector(
  [getSuggestionEntities, getSuggestionResult],
  (entities, results): Array<Suggestion<number>> =>
    results.map(id => entities[id]).filter(value => value != null)
);

export const getSuggestionDropdown = createSelector(
  [getCurrentAccount, readAccountSettings, getUserProperties],
  (account, settings, userProperties) => {
    return {
      generatingSuggestions: !!settings.memory_suggestions,
      suggestionGenerationFailing: !settings.hours_suggestable,
      ui: {
        hasInstalledMemory: !!userProperties[HAS_INSTALLED_MEMORY_TRACKER],
        memoryRetentionDays: account.memory_retention_days,
        hasEnabledTic: !!userProperties.has_enabled_tic,
      },
      account,
    };
  }
);

interface GetSuggestionAsCardsByDay {
  [key: string]: Array<SuggestionAsCard>;
}

export const getSuggestionAsCardsByDay = createSelector(
  [
    getSuggestionEntities,
    getSuggestionResult,
    getProjectVariantMap,
    getUsers,
    getHourDraftSuggestionId,
    getCurrentAccount,
    getMemoryGroupsWithProjects,
    getMemoryEntries,
    getFilter,
    getPinnedProjectIdsSet,
    getRelevantMemoryFilters,
    getSuggesters,
  ],
  (
    entities,
    result,
    projectVariantMap,
    users,
    draftSuggestionId,
    account,
    memoryGroupsSpecifications,
    memoryEntries,
    timelineFilter,
    pinnedProjectIdsSet,
    memoryFilters,
    suggesters
  ): GetSuggestionAsCardsByDay => {
    const currency = account.currency;
    return result.reduce<Record<string, Array<SuggestionAsCard>>>((list, id) => {
      if (!entities || (!!entities && !entities[id])) {
        return list;
      }
      const suggestion = entities[id];
      if (!suggestion || suggestion.status === 'accepted') {
        return list;
      }

      const date = fromISO(suggestion.date).toISODate();
      const variant = projectVariantMap[suggestion.project_id];
      const project =
        enrichProjectVariantOrNull(variant, pinnedProjectIdsSet) ?? makeEmptyEnrichedProject();
      const owner = users.find(u => u.id === suggestion.owner) || suggesters[suggestion.owner];

      const memories: Array<Memory> = memoryEntries.map(entry => {
        return entryToMemoryFactory(entry, {
          filter: memoryFilters,
          selectedEntryIds: suggestion.suggested_entry_ids,
        });
      });

      const memoryGroups = groupMemoriesFactory(memoryGroupsSpecifications, memories, {
        importanceMinThreshold: timelineFilter.importanceMinThreshold,
        expandGroupRefs: timelineFilter.expandGroupRefs,
        expandedEntryIds: timelineFilter.expandedEntryIds,
        activeProjectId: project.base.id,
        operatorMode: OperatorMode.onSuggestion,
        filterUncheckedMemories: true,
      });

      const combinedSuggestion = suggestionAsCardFactory(
        {
          ...suggestion,
          project,
          owner,
        },
        memoryGroups,
        {
          currency,
          isEditing: draftSuggestionId === suggestion.id,
        }
      );

      const suggestions = list[date] ? [...list[date], combinedSuggestion] : [combinedSuggestion];

      list[date] = suggestions;
      return list;
    }, {});
  }
);

export interface MemoryToProjectRelation {
  id: number;
  color: string;
  name: string;
  entryId: number;
}
/**
 * Return references to project for the entry ids on Suggested Hours
 */
export const getMemoryToProjectRelationsForSuggestionsOnCalendarDate = createSelector(
  [getSuggestions, getProjectVariantMap],
  (suggestedHours, projectVariantMap): Record<number, MemoryToProjectRelation> => {
    return suggestedHours.reduce((prev: Record<number, MemoryToProjectRelation>, suggestedHour) => {
      const entryMap = suggestedHour.suggested_entry_ids.reduce<
        Record<number, MemoryToProjectRelation>
      >((prevMappings, entryId) => {
        const variant = projectVariantMap[suggestedHour.project_id];
        if (!variant) {
          return prev;
        }
        const project = extractProjectBase(variant);
        prevMappings[entryId] = {
          id: project.id,
          entryId,
          color: prefixHexColor(getProjectColor(project)),
          name: project.name,
        };
        return prevMappings;
      }, {});

      return { ...prev, ...entryMap };
    }, {});
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);
